import React, { useCallback, useRef, useState } from 'react';
import Button from '../../components/Button';
import Form, { Field } from '../../components/Form';
import LoadingIndicator from '../../components/LoadingIndicator';
import MetaList, { MetaListItem } from '../../components/MetaList';
import {
  PlanInterval,
  PlanTier,
  StartTrialTierInput,
} from '../../graphql/generated/graphql';
import { UIActions } from '../../ui/uiSlice';
import AdminBillingNotice from './AdminBillingNotice';
import styles from './BillingCreateView.module.scss';
import CouponCodeInput from './CouponCodeInput';
import CreditCardInput from './CreditCardInput';
import PlanChooser from './PlanChooser';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import { gql } from '../../graphql/generated/gql';
import type { DocumentType } from '../../graphql/generated/gql';

const BILLING_SUMMARY_FRAGMENT = gql(`
  fragment BillingCreateView_billingSummary on BillingSummary {
    status
    plan {
      tier
      interval
    }
    customer {
      id
      email
    }
    coupon {
      ...PlanChooser_billingCoupon
      ...CouponCodeInput_billingCoupon
    }
    ...AdminBillingNotice_billingSummary
  }
`);

const BILLING_PRICING_FRAGMENT = gql(`
  fragment BillingCreateView_billingPricing on BillingPricing {
    ...PlanChooser_billingPricing
  }
`);

interface BillingCreateViewProps {
  billingSummary: DocumentType<typeof BILLING_SUMMARY_FRAGMENT>;
  billingPricing: DocumentType<typeof BILLING_PRICING_FRAGMENT>;
  isTrialEnded: boolean;
  initialTier: PlanTier | null;
  initialInterval: PlanInterval | null;
  startTrial: (planTier: StartTrialTierInput) => void;
  updatingBilling: boolean;
  createBilling: (params: {
    source: string;
    coupon?: string;
    email?: string;
    planTier: PlanTier;
    planInterval: PlanInterval;
  }) => void;
}

const TIER_TO_START_TIER: Record<string, StartTrialTierInput> = {
  [PlanTier.Startup]: StartTrialTierInput.Startup,
  [PlanTier.Business]: StartTrialTierInput.Business,
};

const BillingCreateView = ({
  billingSummary,
  billingPricing,
  isTrialEnded,
  initialTier,
  initialInterval,
  startTrial,
  updatingBilling,
  createBilling,
}: BillingCreateViewProps) => {
  const dispatch = useAppDispatch();
  const [planTier, setPlanTier] = useState<PlanTier>(
    initialTier ?? billingSummary.plan?.tier ?? PlanTier.Free,
  );
  const [planInterval, setPlanInterval] = useState<PlanInterval>(
    initialInterval ?? billingSummary.plan?.interval ?? PlanInterval.Monthly,
  );
  const [billingCoupon, setBillingCoupon] = useState(billingSummary.coupon);
  const [isCouponShown, setIsCouponShown] = useState(!!billingSummary.coupon);
  const [isEmailShown, setIsEmailShown] = useState(false);
  const [email, setEmail] = useState<string | undefined>(undefined);
  const [loadingToken, setLoadingToken] = useState(false);
  const [card, setCard] =
    useState<stripe.elements.ElementChangeResponse | null>(null);

  const getStripeTokenRef = useRef<
    (() => Promise<stripe.TokenResponse>) | undefined
  >();

  const onBindStripeTokenRef = useCallback(
    (cb?: () => Promise<stripe.TokenResponse>) => {
      getStripeTokenRef.current = cb;
    },
    [],
  );

  const onPlanChange = (
    newPlanTier: PlanTier,
    newPlanInterval: PlanInterval,
  ) => {
    setPlanTier(newPlanTier);
    setPlanInterval(newPlanInterval);
  };

  const handleSubscribe = () => {
    setLoadingToken(true);

    getStripeTokenRef.current?.().then(({ token, error }) => {
      if (error || !token) {
        dispatch(
          UIActions.pushToast({
            level: 'error',
            message: 'Could not verify payment source',
          }),
        );
      } else {
        createBilling({
          source: token.id,
          coupon: billingCoupon?.id,
          email,
          planTier,
          planInterval,
        });
      }

      setLoadingToken(false);
    });
  };

  const canStartTrial = !billingSummary?.startAt;
  const isTrialDisabled = planTier === PlanTier.Free;
  const isSubscribeDisabled = Boolean(
    loadingToken || updatingBilling || !card?.complete || card.error,
  );

  return (
    <div>
      <div className={styles.billingNoticeContainer}>
        <AdminBillingNotice
          billingSummary={billingSummary}
          isTrialEnded={isTrialEnded}
        />
      </div>
      <PlanChooser
        selectedTier={planTier}
        selectedInterval={planInterval}
        onChange={onPlanChange}
        status={billingSummary.status}
        billingPricing={billingPricing}
        billingCoupon={billingCoupon}
      />
      {canStartTrial ? (
        <div className={styles.footer}>
          <Button
            variant="primary"
            disabled={isTrialDisabled}
            onClick={() => {
              const startTier = TIER_TO_START_TIER[planTier];
              if (startTier) {
                startTrial(startTier);
              }
            }}
          >
            Start trial
          </Button>
        </div>
      ) : (
        <>
          {isCouponShown ? (
            <>
              <div className={styles.sectionWrapper}>
                <strong>Coupon code</strong>
              </div>
              <CouponCodeInput
                billingCoupon={billingCoupon}
                onApply={(c) => setBillingCoupon(c)}
              />
            </>
          ) : (
            <Button
              variant="link"
              size="small"
              onClick={() => setIsCouponShown(true)}
            >
              Add coupon code
            </Button>
          )}
          <div className={styles.sectionWrapper}>
            <strong>Add a payment method</strong>
          </div>
          <CreditCardInput
            onBind={onBindStripeTokenRef}
            // @ts-expect-error
            onChange={setCard}
          />
          {(isEmailShown && (
            <>
              <div className={styles.sectionWrapper}>
                <strong>Billing email</strong>
              </div>
              <Form initialValues={{ email }}>
                <Field
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setEmail(e.target.value)
                  }
                />
              </Form>
            </>
          )) || (
            <div className={styles.meta}>
              <MetaList>
                <MetaListItem>
                  Receipts and invoices will be sent to{' '}
                  {billingSummary.customer.email}
                </MetaListItem>
                <MetaListItem>
                  <Button
                    variant="link"
                    onClick={() => setIsEmailShown(true)}
                    size="micro"
                  >
                    Change
                  </Button>
                </MetaListItem>
              </MetaList>
            </div>
          )}
          <div className={styles.footer}>
            <Button
              variant="primary"
              disabled={isSubscribeDisabled}
              onClick={handleSubscribe}
            >
              {loadingToken || updatingBilling ? (
                <LoadingIndicator delay={0} adaptColor />
              ) : (
                'Subscribe'
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default BillingCreateView;
